/**
 * FreeDashboard Component - Main dashboard for free tier users
 * 
 * Updated: 2025-01-16 (Dashboard Redesign Phase 2 + Mock Data Refactor)
 * New Layout: Header + Sidebar + Main Content
 * New Cards: TaxSavingsSummary, ImplementationProgress, QuickWins, TaxCalendar
 * Mock Data: Imported from constants/mockData.js (no hardcoding)
 */

import React, { useState, useEffect } from 'react';
import { useTier } from '../contexts/TierContext';
import { askSaraAPI, userAPI } from '../services/api';
import QuotaDisplay from '../components/QuotaDisplay';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import Sidebar from '../components/dashboard/Sidebar';
import EmailVerificationBanner from '../components/EmailVerificationBanner'; // Phase 4.8: Email Verification
import TaxSavingsSummary from '../components/dashboard/TaxSavingsSummary';
import ImplementationProgress from '../components/dashboard/ImplementationProgress';
import QuickWins from '../components/dashboard/QuickWins';
import TaxCalendar from '../components/dashboard/TaxCalendar';
import PopularTopics from '../components/dashboard/PopularTopics';
import HelpSection from '../components/dashboard/HelpSection';

// Import mock data constants (centralized, not hardcoded)
import {
  MOCK_TAX_SAVINGS_SUMMARY,
  MOCK_IMPLEMENTATION_PROGRESS,
  MOCK_QUICK_WINS,
  MOCK_TAX_CALENDAR,
} from '../constants/mockData';

const FreeDashboard = () => {
  const { user, tierInfo, refreshTierInfo, logout } = useTier();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState([]);
  const [disclaimer, setDisclaimer] = useState('');
  const [fromCache, setFromCache] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [useStreaming, setUseStreaming] = useState(true); // Phase 4.5: Streaming toggle
  const [isStreaming, setIsStreaming] = useState(false); // Phase 4.5: Active streaming indicator
  const [streamStatus, setStreamStatus] = useState(''); // Phase 4.5: Streaming status message

  // Check upgrade eligibility on mount
  useEffect(() => {
    checkUpgradeEligibility();
  }, []);

  const checkUpgradeEligibility = async () => {
    try {
      const response = await userAPI.checkUpgradeEligibility();
      if (response.data.should_show_prompt) {
        setShowUpgradePrompt(true);
      }
    } catch (err) {
      console.error('Failed to check upgrade eligibility:', err);
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setError('');
    setLoading(true);
    setAnswer('');
    setSources([]);
    setDisclaimer('');
    setFromCache(false);
    setStreamStatus('');

    // Phase 4.5: Route to streaming or non-streaming
    if (useStreaming) {
      await handleStreamingQuestion();
    } else {
      await handleRegularQuestion();
    }
  };

  // Phase 4.5: Regular (non-streaming) question handler
  const handleRegularQuestion = async () => {
    try {
      const response = await askSaraAPI.askQuestion(question);
      
      // Set all RAG response fields
      setAnswer(response.data.answer);
      setSources(response.data.sources || []);
      setDisclaimer(response.data.disclaimer || '');
      setFromCache(response.data.from_cache || false);
      setQuestion('');
      
      // Refresh tier info to update quota
      await refreshTierInfo();
      
      // Check if should show upgrade prompt after question
      await checkUpgradeEligibility();
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Monthly quota exceeded. Please upgrade to Premium for unlimited questions.');
        setShowUpgradePrompt(true);
      } else if (err.response?.status === 422) {
        // Handle Pydantic validation errors
        const detail = err.response?.data?.detail;
        if (Array.isArray(detail)) {
          setError(detail.map(e => e.msg).join(', '));
        } else {
          setError(detail || 'Validation error');
        }
      } else {
        setError(err.response?.data?.detail || 'Failed to ask question');
      }
    } finally {
      setLoading(false);
    }
  };

  // Phase 4.5: Streaming question handler with SSE
  const handleStreamingQuestion = async () => {
    setIsStreaming(true);
    setStreamStatus('Connecting...');
    
    const backend_url = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${backend_url}/api/asksara/ask-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ question: question })
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          setError('Monthly quota exceeded. Please upgrade to Premium for unlimited questions.');
          setShowUpgradePrompt(true);
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.detail || 'Failed to start streaming');
        }
        setIsStreaming(false);
        setLoading(false);
        return;
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedAnswer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          setIsStreaming(false);
          setLoading(false);
          setStreamStatus('');
          setQuestion('');
          
          // Refresh tier info to update quota
          await refreshTierInfo();
          await checkUpgradeEligibility();
          break;
        }
        
        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        lines.forEach((line, index) => {
          if (line.startsWith('event:')) {
            const eventType = line.substring(6).trim();
            
            // Next line should be data
            const dataLine = lines[index + 1];
            if (dataLine && dataLine.startsWith('data:')) {
              const data = JSON.parse(dataLine.substring(5).trim());
              
              switch (eventType) {
                case 'start':
                  setStreamStatus('Processing...');
                  break;
                  
                case 'status':
                  if (data.status === 'classifying_intent') {
                    setStreamStatus('Understanding your question...');
                  } else if (data.status === 'generating_response') {
                    setStreamStatus(`Generating answer (${data.agent})...`);
                  }
                  break;
                  
                case 'intent':
                  setStreamStatus(`Routing to ${data.intent} specialist...`);
                  break;
                  
                case 'token':
                  streamedAnswer += data.token;
                  setAnswer(streamedAnswer);
                  break;
                  
                case 'sources':
                  setSources(data.sources || []);
                  setFromCache(data.from_cache || false);
                  break;
                  
                case 'metadata':
                  setDisclaimer(data.disclaimer || '');
                  setStreamStatus('Finalizing...');
                  break;
                  
                case 'done':
                  setStreamStatus('Complete!');
                  break;
                  
                case 'error':
                  setError(data.error || 'Streaming error occurred');
                  setIsStreaming(false);
                  setLoading(false);
                  break;
              }
            }
          }
        });
      }
      
    } catch (err) {
      console.error('Streaming error:', err);
      setIsStreaming(false);
      setLoading(false);
      setError('Streaming failed. Please try regular mode.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dashboard Header */}
      <DashboardHeader />

      {/* Layout: Sidebar + Main Content */}
      <div className="flex">
        {/* Sidebar Navigation */}
        <Sidebar currentPath="/dashboard" />

        {/* Main Content Area */}
        <main className="flex-1 lg:ml-64 p-6 pt-6">
          <div className="max-w-7xl mx-auto">
            {/* Email Verification Banner - Phase 4.8 */}
            <EmailVerificationBanner user={user} />
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tax Savings Summary - Full Width */}
            <TaxSavingsSummary 
              totalSavings={MOCK_TAX_SAVINGS_SUMMARY.totalSavings}
              roi={MOCK_TAX_SAVINGS_SUMMARY.roi}
              investment={MOCK_TAX_SAVINGS_SUMMARY.investment}
            />

            {/* Upgrade Prompt */}
            {showUpgradePrompt && (
              <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg shadow-lg p-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold mb-2">Upgrade to Premium</h3>
                    <p className="text-sm mb-4">
                      Get unlimited AskSara questions and access to all premium features
                    </p>
                    <button className="bg-white text-purple-600 px-6 py-2 rounded-md font-medium hover:bg-gray-100 transition">
                      Upgrade Now - $2,499
                    </button>
                  </div>
                  <button
                    onClick={() => setShowUpgradePrompt(false)}
                    className="text-white hover:text-gray-200"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {/* Implementation Progress Card */}
            <ImplementationProgress 
              overallProgress={MOCK_IMPLEMENTATION_PROGRESS.overallProgress}
              strategies={MOCK_IMPLEMENTATION_PROGRESS.strategies}
            />

            {/* Quick Wins Card */}
            <QuickWins 
              actions={MOCK_QUICK_WINS.actions}
            />

            {/* Popular Topics - NEW Phase 3 */}
            <PopularTopics onTopicClick={(topicQuestion) => {
              setQuestion(topicQuestion);
              // Scroll to textarea
              document.querySelector('textarea')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }} />

            {/* Ask Question */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Ask AskSara</h2>
                {/* Phase 4.5: Streaming Toggle */}
                <label className="flex items-center cursor-pointer">
                  <span className="text-sm text-gray-600 mr-2">Streaming</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={useStreaming}
                      onChange={(e) => setUseStreaming(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`block w-10 h-6 rounded-full transition ${useStreaming ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                    <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform ${useStreaming ? 'translate-x-4' : ''}`}></div>
                  </div>
                </label>
              </div>
              <form onSubmit={handleAskQuestion} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
                
                {/* Phase 4.5: Streaming Status Indicator */}
                {isStreaming && streamStatus && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      <p className="text-sm text-blue-800">{streamStatus}</p>
                    </div>
                  </div>
                )}
                
                <div>
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask your tax strategy question..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows="4"
                    disabled={loading}
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading || !question.trim()}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Asking...' : 'Ask Question'}
                </button>
              </form>

              {/* Answer Display */}
              {answer && (
                <div className="mt-6 space-y-4">
                  {/* Cache Indicator */}
                  {fromCache && (
                    <div className="flex items-center text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Answered from cache (faster response)
                    </div>
                  )}
                  
                  {/* AI Answer */}
                  <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-blue-900 mb-3 text-lg flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      AskSara's Answer:
                    </h3>
                    <div className="text-gray-800 leading-relaxed whitespace-pre-wrap text-sm max-h-96 overflow-y-auto">
                      {answer}
                    </div>
                  </div>

                  {/* Sources/Citations */}
                  {sources && sources.length > 0 && (
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center text-sm">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        Tax Strategies Referenced:
                      </h4>
                      <div className="space-y-2">
                        {sources.map((source, index) => (
                          <div key={index} className="flex items-start p-2 bg-gray-50 rounded text-xs">
                            <span className="font-medium text-blue-600 mr-2">{index + 1}.</span>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{source.strategy_name}</div>
                              {source.irc_section && (
                                <div className="text-gray-600 text-xs">IRC Section: {source.irc_section}</div>
                              )}
                              {source.relevance_score && (
                                <div className="text-gray-500 text-xs">
                                  Relevance: {(source.relevance_score * 100).toFixed(0)}%
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Disclaimer */}
                  {disclaimer && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-yellow-900 text-sm mb-1">Important Notice</h4>
                          <p className="text-yellow-800 text-xs leading-relaxed">{disclaimer}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Free Tier Benefits */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Your Free Tier Includes</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>5 AskSara questions per month</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Access to free dashboard</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">✗</span>
                  <span className="text-gray-500">Document uploads</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">✗</span>
                  <span className="text-gray-500">Strategy generation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">✗</span>
                  <span className="text-gray-500">ROI tracking</span>
                </li>
              </ul>
              <button className="mt-4 w-full bg-purple-600 text-white py-2 px-4 rounded-md font-medium hover:bg-purple-700">
                Unlock All Features
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <QuotaDisplay />

            {/* Tax Calendar Card */}
            <TaxCalendar 
              deadlines={MOCK_TAX_CALENDAR.deadlines}
            />

            {/* Account Info */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-lg font-semibold mb-3">Account Info</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">{user?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tier:</span>
                  <span className="font-medium capitalize">{tierInfo?.tier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Days on free:</span>
                  <span className="font-medium">{tierInfo?.days_on_free_tier || 0}</span>
                </div>
              </div>
            </div>
          </div>
            </div>
          </div>
        </main>
      </div>

      {/* Help Section - Sticky bottom-left - NEW Phase 3 */}
      <HelpSection />
    </div>
  );
};

export default FreeDashboard;
